/**
 * BHCR Inline Product Configurator (<bhcr-card-configurator>)
 *
 * Lets shoppers pick the same purchasing options offered on the product detail
 * page (e.g. Grind / Weight / Quantity) directly from a product card, before
 * the item is added to the cart.
 *
 * Interaction model:
 * - The "Add to cart + price" button is the single CTA. The first click expands
 *   the configurator (a black panel that grows upward out of the button). Once
 *   the options are set, clicking the same button again adds the configured
 *   variant to the cart.
 *
 * Reuse / architecture:
 * - Submission mirrors the PDP <product-form> flow (same /cart/add endpoint,
 *   section rendering and cart-drawer opening) but is handled here so the single
 *   trigger button can own both the open and the confirm interactions.
 * - Quantity uses the shared <quantity-input> element (assets/global.js).
 * - Variant resolution is done client-side from a JSON blob embedded in the
 *   card. The PDP resolves variants server-side (section re-fetch), which is
 *   too slow for a grid; cards get a lightweight matcher instead. The PDP is
 *   left untouched.
 */
if (!customElements.get('bhcr-card-configurator')) {
  // Only one configurator may be open at a time across the whole page.
  let activeConfigurator = null;

  /**
   * Standard Shopify money formatter so quantity changes can render an accurate
   * line total from raw variant cents. Falls back gracefully if no format is
   * provided.
   */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';

      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    }

    const match = formatString.match(placeholderRegex);
    if (!match) return formatString;

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  customElements.define(
    'bhcr-card-configurator',
    class BhcrCardConfigurator extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.trigger = this.querySelector('[data-config-trigger]');
        this.panel = this.querySelector('[data-config-panel]');
        this.closeBtn = this.querySelector('[data-config-close]');
        this.form = this.querySelector('form');
        this.variantInput = this.form ? this.form.querySelector('input[name="id"]') : null;
        this.labelEl = this.trigger ? this.trigger.querySelector('.product-card__cta-label') : null;
        this.priceTargets = this.querySelectorAll('[data-config-price]');
        this.errorEl = this.querySelector('.product-card__config-error');
        this.selects = Array.from(this.querySelectorAll('[data-card-option]'));
        this.qtyInput = this.querySelector('.product-card__qty-input');

        if (!this.trigger || !this.panel || !this.form || !this.variantInput) {
          return;
        }

        // The hidden variant input ships disabled (Shopify form convention); we
        // own it now, so enable it to keep it in the submitted FormData.
        this.variantInput.disabled = false;

        this.variants = this.parseVariants();
        this.moneyFormat = this.parseMoneyFormat();
        this.submitting = false;

        // Labels for the single CTA.
        this.addLabel = (this.trigger.dataset.labelAdd || (this.labelEl ? this.labelEl.textContent : '')).trim();
        this.addingLabel = this.trigger.dataset.labelAdding || this.addLabel;
        this.addedLabel = this.trigger.dataset.labelAdded || this.addLabel;
        this.errorLabel = this.trigger.dataset.labelError || this.addLabel;

        // Collapsed state shows the original "from" price; remember it to restore.
        const priceEl = this.priceTargets[0];
        this.defaultPriceHtml = priceEl ? priceEl.innerHTML : '';
        this.defaultAriaLabel = this.trigger.getAttribute('aria-label') || '';

        this._onTriggerClick = this.onTriggerClick.bind(this);
        this._onOptionChange = this.onOptionChange.bind(this);
        this._onQtyChange = this.onQtyChange.bind(this);
        this._onCloseClick = this.onCloseClick.bind(this);
        this._onDocumentClick = this.onDocumentClick.bind(this);
        this._onKeyDown = this.onKeyDown.bind(this);

        this.trigger.addEventListener('click', this._onTriggerClick);
        this.selects.forEach((select) => select.addEventListener('change', this._onOptionChange));
        if (this.qtyInput) this.qtyInput.addEventListener('change', this._onQtyChange);
        if (this.closeBtn) this.closeBtn.addEventListener('click', this._onCloseClick);

        this.updateSelection();
      }

      disconnectedCallback() {
        if (activeConfigurator === this) activeConfigurator = null;
        this.removeDocumentListeners();
      }

      parseVariants() {
        const node = this.querySelector('script[data-card-variants]');
        if (!node) return [];
        try {
          return JSON.parse(node.textContent) || [];
        } catch (e) {
          return [];
        }
      }

      parseMoneyFormat() {
        const node = this.querySelector('script[data-money-format]');
        if (!node) return window.moneyFormat || '';
        try {
          return JSON.parse(node.textContent) || '';
        } catch (e) {
          return window.moneyFormat || '';
        }
      }

      /* ----- Variant matching (client-side) -------------------------------- */

      getSelectedValues() {
        return this.selects
          .slice()
          .sort(
            (a, b) =>
              (parseInt(a.dataset.optionPosition, 10) || 0) - (parseInt(b.dataset.optionPosition, 10) || 0)
          )
          .map((select) => select.value);
      }

      findVariant(values) {
        return (
          this.variants.find((variant) => {
            if (!variant.options || variant.options.length !== values.length) return false;
            return variant.options.every((opt, i) => opt === values[i]);
          }) || null
        );
      }

      updateOptionAvailability(currentValues) {
        this.selects.forEach((select) => {
          const position = parseInt(select.dataset.optionPosition, 10) || 0;
          Array.from(select.options).forEach((optionEl) => {
            const candidate = currentValues.slice();
            candidate[position] = optionEl.value;
            const variant = this.findVariant(candidate);
            const available = !!(variant && variant.available);
            // Keep the currently-selected value selectable so the control can
            // always represent its own state (availability shown via the CTA).
            optionEl.disabled = !available && optionEl.value !== select.value;
          });
        });
      }

      updateSelection() {
        const values = this.getSelectedValues();
        this.updateOptionAvailability(values);

        const variant = this.findVariant(values);
        this.currentVariant = variant;

        if (variant) {
          this.variantInput.value = variant.id;
          this.updateQuantityRules(variant);
        } else {
          this.variantInput.value = '';
        }

        this.updatePriceAndLabel();
      }

      /** Reflects the current variant + quantity onto the CTA — but only while
       *  open. Collapsed cards keep their original "from" price. */
      updatePriceAndLabel() {
        if (!this.isOpen() || this.submitting) return;
        const strings = window.variantStrings || {};
        const variant = this.currentVariant;

        if (variant && variant.available) {
          this.setLabel(this.addLabel);
          this.trigger.setAttribute('aria-disabled', 'false');
          this.setLineTotal(variant);
        } else if (variant) {
          this.setLabel(strings.soldOut || 'Sold out');
          this.trigger.setAttribute('aria-disabled', 'true');
        } else {
          this.setLabel(strings.unavailable || 'Unavailable');
          this.trigger.setAttribute('aria-disabled', 'true');
        }
        this.syncAriaLabel();
      }

      /** Keep the button's accessible name in sync with the visible label +
       *  price while open. */
      syncAriaLabel() {
        if (!this.isOpen()) return;
        const label = this.labelEl ? this.labelEl.textContent.trim() : '';
        const priceEl = this.priceTargets[0];
        const price = priceEl ? priceEl.textContent.trim() : '';
        const text = price ? `${label} — ${price}` : label;
        if (text) this.trigger.setAttribute('aria-label', text);
      }

      setLineTotal(variant) {
        const qty = Math.max(1, parseInt(this.qtyInput && this.qtyInput.value, 10) || 1);
        if (typeof variant.price === 'number' && this.moneyFormat) {
          this.setPrice(formatMoney(variant.price * qty, this.moneyFormat));
        } else if (variant.price_formatted) {
          this.setPrice(variant.price_formatted);
        }
      }

      setPrice(formatted) {
        if (formatted == null) return;
        this.priceTargets.forEach((el) => {
          if (window.BhcrDigitAnimate) {
            BhcrDigitAnimate.set(el, formatted);
          } else {
            el.textContent = formatted;
          }
        });
      }

      restoreDefaultPrice() {
        this.priceTargets.forEach((el) => {
          el.innerHTML = this.defaultPriceHtml;
        });
      }

      setLabel(label) {
        if (this.labelEl && label != null) this.labelEl.textContent = label;
      }

      updateQuantityRules(variant) {
        if (!this.qtyInput || !variant || !variant.quantity_rule) return;
        const rule = variant.quantity_rule;
        const min = rule.min || 1;
        const inc = rule.increment || 1;

        this.qtyInput.min = String(min);
        this.qtyInput.step = String(inc);
        this.qtyInput.dataset.min = String(min);

        if (rule.max != null) {
          this.qtyInput.max = String(rule.max);
          this.qtyInput.dataset.max = String(rule.max);
        } else {
          this.qtyInput.removeAttribute('max');
          delete this.qtyInput.dataset.max;
        }

        let current = parseInt(this.qtyInput.value, 10);
        if (isNaN(current) || current < min) current = min;
        if (rule.max != null && current > rule.max) current = rule.max;
        this.qtyInput.value = String(current);

        // Let the shared <quantity-input> revalidate the +/- button states.
        this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onOptionChange() {
        this.updateSelection();
      }

      onQtyChange() {
        // Recompute the line total when the quantity changes.
        this.updatePriceAndLabel();
      }

      /* ----- Open / close -------------------------------------------------- */

      isOpen() {
        return this.classList.contains('is-open');
      }

      onTriggerClick(event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.isOpen()) {
          this.addToCart();
        } else {
          this.open();
        }
      }

      onCloseClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.close({ returnFocus: false });
      }

      open() {
        if (this.isOpen()) return;

        if (activeConfigurator && activeConfigurator !== this) {
          activeConfigurator.close({ returnFocus: false });
        }
        activeConfigurator = this;

        this.hideError();
        this.panel.removeAttribute('inert');
        this.classList.add('is-open');
        this.trigger.setAttribute('aria-expanded', 'true');

        // Refresh in case cart contents / availability changed since render.
        this.updateSelection();

        // Defer document listeners so the opening click doesn't close it.
        window.requestAnimationFrame(() => {
          this.addDocumentListeners();
          this.focusFirstControl();
        });
      }

      close(options = {}) {
        if (!this.isOpen()) return;
        const returnFocus = options.returnFocus !== false;
        const focusVisible = options.focusVisible === true;

        this.classList.remove('is-open');
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.setAttribute('aria-disabled', 'false');
        this.panel.setAttribute('inert', '');
        this.removeDocumentListeners();
        this.hideError();

        // Back to the resting CTA: original "from" price + add label.
        this.setLabel(this.addLabel);
        this.restoreDefaultPrice();
        if (this.defaultAriaLabel) this.trigger.setAttribute('aria-label', this.defaultAriaLabel);

        if (activeConfigurator === this) activeConfigurator = null;

        if (returnFocus && this.trigger) {
          try {
            this.trigger.focus({ preventScroll: true, focusVisible });
          } catch {
            this.trigger.focus({ preventScroll: true });
          }
        } else {
          const focused = this.querySelector(':focus');
          if (focused && typeof focused.blur === 'function') focused.blur();
        }
      }

      focusFirstControl() {
        if (!this.isOpen()) return;
        const first = this.querySelector(
          '[data-card-option]:not([aria-disabled="true"]), .product-card__qty-input'
        );
        if (first && typeof first.focus === 'function') {
          first.focus({ preventScroll: true });
        }
      }

      addDocumentListeners() {
        document.addEventListener('click', this._onDocumentClick);
        document.addEventListener('keydown', this._onKeyDown);
      }

      removeDocumentListeners() {
        document.removeEventListener('click', this._onDocumentClick);
        document.removeEventListener('keydown', this._onKeyDown);
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) this.close({ returnFocus: false });
      }

      onKeyDown(event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
          event.stopPropagation();
          this.close({ focusVisible: true });
        }
      }

      /* ----- Add to cart --------------------------------------------------- */

      addToCart() {
        if (this.submitting) return;

        const variant = this.currentVariant;
        if (!variant || !variant.available) {
          // Reflect unavailability and bail without submitting.
          this.updatePriceAndLabel();
          return;
        }

        this.submitting = true;
        this.hideError();
        this.trigger.classList.add('is-loading');
        this.trigger.setAttribute('aria-busy', 'true');
        this.setLabel(this.addingLabel);

        const cart = document.querySelector('cart-drawer');
        const formData = new FormData(this.form);
        const variantId = formData.get('id');

        if (cart) {
          const sectionKeys = window.BhcrCartPricing?.getCartSectionApiKeys?.({ includeFooter: true }) || [
            'cart-drawer',
          ];
          formData.append('sections', sectionKeys.join(','));
          formData.append('sections_url', window.location.pathname);
        }

        const addRequest = window.BhcrCart
          ? BhcrCart.add(formData)
          : fetch((typeof routes !== 'undefined' && routes.cart_add_url) || '/cart/add', {
              method: 'POST',
              headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: formData,
            }).then((response) => response.json());

        addRequest
          .then((response) => {
            if (response.status && Number(response.status) >= 400) {
              this.showError(response.description || response.message || '');
              this.setLabel(this.errorLabel);
              return;
            }

            this.setLabel(this.addedLabel);

            if (cart) {
              if (window.BhcrCart?.syncCartUI) {
                BhcrCart.syncCartUI(response);
              } else if (window.BhcrCart) {
                BhcrCart.syncDrawer(cart, response);
              } else if (typeof cart.renderContents === 'function') {
                cart.renderContents(response);
              }
              if (typeof cart.open === 'function') cart.open();
            }

            // Notify other listeners (analytics, etc.). The drawer is already
            // updated above, so its subscriber ignores this source.
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-card-configurator',
                productVariantId: variantId,
                cartData: response,
              });
            }

            this.resetAfterAdd();
          })
          .catch((err) => {
            const rateMsg = window.cartStrings?.rateLimit;
            if (err?.message === 'rate_limited' && rateMsg) {
              this.showError(rateMsg);
            }
            this.setLabel(this.errorLabel);
          })
          .finally(() => {
            this.submitting = false;
            this.trigger.classList.remove('is-loading');
            this.trigger.removeAttribute('aria-busy');
          });
      }

      resetAfterAdd() {
        if (this.qtyInput) {
          this.qtyInput.value = this.qtyInput.min || '1';
          this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        // The cart drawer is the confirmation; collapse the configurator.
        this.close({ returnFocus: false });
      }

      showError(message) {
        if (!this.errorEl) return;
        const span = this.errorEl.querySelector('span');
        if (span) span.textContent = message || '';
        this.errorEl.hidden = false;
      }

      hideError() {
        if (!this.errorEl) return;
        this.errorEl.hidden = true;
        const span = this.errorEl.querySelector('span');
        if (span) span.textContent = '';
      }
    }
  );
}
